// NOMBASE — lazyWithRetry
//
// Wraps React.lazy so that a code-split chunk that fails to load (almost
// always because a NEW deploy rotated the chunk hashes and a stale client
// is still asking for the old filename) auto-recovers by reloading the page
// ONCE to pull the fresh index.html + chunk manifest.
//
// Symptom this fixes: after a production deploy, an already-open tab clicks
// into a not-yet-loaded route (e.g. /ingredients/:id) → `import()` 404s →
// `ChunkLoadError` bubbles to the root error boundary → black "Something
// went wrong" screen. Without this, the only recovery is a manual refresh.
//
// Loop guard: we only auto-reload at most once per 10s window (tracked in
// sessionStorage). If the chunk is genuinely gone after a reload, the real
// error is re-thrown so the boundary can show its fallback instead of
// reloading forever.

import { lazy } from 'react';

const RETRY_KEY = 'nb_chunk_reload_at';
const RETRY_WINDOW_MS = 10000;

export function isChunkLoadError(err) {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const msg = String(err.message || err);
  return (
    /Loading chunk \S+ failed/i.test(msg) ||
    /Loading CSS chunk \S+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /'text\/html' is not a valid JavaScript MIME type/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

export function lazyWithRetry(importFn) {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (err) {
      if (isChunkLoadError(err)) {
        let last = 0;
        try { last = Number(window.sessionStorage.getItem(RETRY_KEY) || 0); } catch { /* no storage */ }
        const now = Date.now();
        if (now - last > RETRY_WINDOW_MS) {
          try { window.sessionStorage.setItem(RETRY_KEY, String(now)); } catch { /* no storage */ }
          window.location.reload();
          // Block render until the reload happens so the boundary never flashes.
          return new Promise(() => {});
        }
      }
      throw err;
    }
  });
}

export default lazyWithRetry;
